import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import {
  Package, Truck, TrendingUp, Store, Globe, ArrowRight, PlayCircle, Quote,
  BadgeCheck, Boxes, Headphones, ShoppingBag, Sparkles,
} from "lucide-react";
import api, { fmtINR, fmtDate, imgUrl } from "@/lib/api";
import { DP, IMG, SiteShell, Reveal, SectionHead, Counter, GreenBtn, GhostBtn, ProductCard, Rating } from "./site";

const TRUST = [
  [Boxes, 10000, "+", "Active Partners", DP.green],
  [Package, 1000000, "+", "Products", DP.royal],
  [BadgeCheck, 99, "%", "Order Success", DP.bright],
  [Headphones, 24, "/7", "Support", DP.green],
];

const CATEGORY_ART = [
  ["Electronics", "🎧"], ["Fashion", "👕"], ["Home & Living", "🛋️"], ["Beauty & Personal Care", "💄"],
  ["Kitchen & Appliances", "🍳"], ["Toys & Kids", "🧸"], ["Health & Wellness", "🌿"], ["Sports & Fitness", "🏋️"],
  ["Automotive", "🚗"], ["More Categories", "➕"],
];

const PROMOS = [
  { title: "Sell On Top Marketplaces", desc: "Amazon · Flipkart · Meesho — reach crores of customers without maintaining your own inventory.", img: IMG.marketplaces, cta: "Get Started", to: "/become-a-partner", testid: "promo-marketplaces" },
  { title: "Launch Your Own Online Store", desc: "Create your own professional online store with your brand, your domain and beautiful templates.", img: IMG.store, cta: "Create Your Store", to: "/partner-plans", testid: "promo-store" },
  { title: "Earn More With Trending Products", desc: "Discover high-demand products with live margins and maximize your earning potential.", img: IMG.trending, cta: "Explore Products", to: "/shop", testid: "promo-trending" },
];

const STEPS = [
  ["01", "Choose Products", "Select products from our verified catalog with real stock and margins.", DP.royal],
  ["02", "List & Sell", "Sell through Amazon, Flipkart, Meesho or your own online store.", DP.bright],
  ["03", "We Handle The Rest", "Packing, shipping, tracking and returns — operations are on us.", DP.green],
  ["04", "You Grow", "Focus on customers, sales and growing your business profits.", "#059669"],
];

const TESTIMONIALS = [
  ["Ramesh K.", "Hyderabad", "DropPartner helped me start my online business with zero investment. The support team is amazing — my first sale came in week one.", 5],
  ["Priya S.", "Bengaluru", "I now sell on Amazon and my own store together. Earnings are growing every single month and I never touch inventory.", 5],
  ["Amit T.", "Mumbai", "Easy to use, great products and excellent support. The marketplace approval flow is genuinely hands-off. Highly recommended!", 4.5],
];

const FLOATERS = [
  ["Sell on Amazon", "top-6 -left-2 sm:-left-6", DP.royal, 0],
  ["Sell on Flipkart", "top-24 -right-2 sm:-right-8", DP.bright, 0.4],
  ["Your Own Store", "bottom-24 -left-3 sm:-left-10", DP.green, 0.8],
  ["Track Orders", "bottom-10 -right-2 sm:-right-6", "#0891B2", 1.2],
  ["Grow Your Income", "-bottom-6 left-1/4", "#059669", 1.6],
];

export default function DPHome() {
  const [tab, setTab] = useState("All");
  const { data: cats } = useQuery({ queryKey: ["dp-cats"], queryFn: async () => (await api.get("/public/catalog/categories")).data });
  const { data: trending } = useQuery({ queryKey: ["dp-trending"], queryFn: async () => (await api.get("/public/catalog/trending")).data });
  const { data: blog } = useQuery({ queryKey: ["dp-blog"], queryFn: async () => (await api.get("/public/blog")).data });

  const trendItems = trending?.items || [];
  const tabCats = ["All", ...new Set(trendItems.map((p) => p.category))].slice(0, 7);
  const shown = tab === "All" ? trendItems : trendItems.filter((p) => p.category === tab);
  const catCards = (cats?.items?.length ? cats.items : CATEGORY_ART.map(([n]) => ({ name: n, count: 0, image: "" }))).slice(0, 10);

  return (
    <SiteShell>
      {/* HERO */}
      <section className="relative overflow-hidden py-14 sm:py-20" data-testid="hero"
        style={{ background: `radial-gradient(1000px 480px at 80% 10%, rgba(37,99,235,0.12), transparent), radial-gradient(700px 400px at 5% 90%, rgba(16,185,129,0.1), transparent), ${DP.ice}` }}>
        <div className="mx-auto grid max-w-7xl items-center gap-10 px-4 lg:grid-cols-2">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span className="inline-flex items-center gap-2 rounded-full border bg-white px-4 py-1.5 text-[11px] font-extrabold uppercase tracking-[0.18em] shadow-sm"
              style={{ borderColor: DP.border, color: DP.royal }} data-testid="hero-label">
              <Sparkles size={12} style={{ color: DP.green }} /> Your Partner in Online Business
            </span>
            <h1 className="mt-5 text-4xl font-extrabold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl" style={{ fontFamily: "Outfit, sans-serif" }} data-testid="hero-heading">
              <span style={{ color: DP.royal }}>Drop</span><span style={{ color: DP.green }}>Partner</span>
              <span className="mt-1 block text-2xl sm:text-3xl lg:text-4xl" style={{ color: DP.text }}>Choose. Sell. Grow.</span>
            </h1>
            <p className="mt-4 text-lg font-bold" style={{ color: DP.royal }}>Start Your Online Business With <span style={{ color: DP.green }}>Zero Inventory</span></p>
            <p className="mt-3 max-w-lg text-sm leading-relaxed sm:text-base" style={{ color: DP.muted }}>
              Choose from premium products and sell on India's leading marketplaces or your own online store.
              No warehouse. No inventory headache. No packing or shipping stress.
            </p>
            <div className="mt-5 flex flex-wrap gap-x-5 gap-y-2">
              {[["📦", "No Inventory"], ["📦", "No Packing"], ["🚚", "No Shipping"], ["📈", "Grow Your Profits"]].map(([e, t]) => (
                <span key={t} className="flex items-center gap-1.5 text-xs font-bold" style={{ color: DP.text }}>{e} {t}</span>
              ))}
            </div>
            <div className="mt-7 flex flex-wrap gap-3">
              <GreenBtn to="/register" testid="hero-start-selling">Start Selling Now</GreenBtn>
              <GhostBtn to="/how-it-works" testid="hero-how-it-works"><PlayCircle size={16} /> See How It Works</GhostBtn>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7, delay: 0.15 }}
            className="relative mx-auto w-full max-w-md lg:max-w-none">
            <div className="pointer-events-none absolute -inset-8 rounded-[3rem] opacity-40 blur-3xl"
              style={{ background: `linear-gradient(135deg, ${DP.bright}55, ${DP.green}44)` }} />
            <div className="relative overflow-hidden rounded-[2rem] border bg-white p-4 shadow-[0_36px_80px_rgba(11,25,44,0.18)]" style={{ borderColor: DP.border }} data-testid="hero-3d-card">
              <img src={IMG.hero} alt="DropPartner 3D ecommerce ecosystem" className="w-full rounded-[1.5rem] object-cover" />
            </div>
            {FLOATERS.map(([label, pos, color, d], i) => (
              <motion.span key={label} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 + i * 0.12 }}
                data-testid={`hero-chip-${label.toLowerCase().replace(/ /g, "-")}`}
                className={`absolute ${pos} rounded-xl border bg-white/95 px-3 py-2 text-[11px] font-extrabold shadow-lg backdrop-blur`}
                style={{ color, borderColor: `${color}44`, animation: `dpfloat 4.5s ease-in-out ${d}s infinite` }}>
                {label}
              </motion.span>
            ))}
          </motion.div>
        </div>
      </section>

      {/* TRUST BAR */}
      <section className="border-y bg-white py-8" style={{ borderColor: DP.border }} data-testid="trust-bar">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-center gap-x-10 gap-y-5 px-4">
          <div className="flex items-center gap-3">
            <span className="text-[10px] font-extrabold uppercase tracking-[0.2em]" style={{ color: DP.muted }}>Sell On</span>
            {["Amazon", "Flipkart", "Meesho"].map((m) => (
              <span key={m} className="rounded-lg border px-3 py-1.5 text-xs font-extrabold shadow-sm" style={{ borderColor: DP.border, color: DP.royal }}>{m}</span>
            ))}
            <span className="flex items-center gap-1 rounded-lg border px-3 py-1.5 text-xs font-extrabold shadow-sm" style={{ borderColor: `${DP.green}55`, color: DP.greenD }}>
              <Store size={12} /> Your Own Store
            </span>
          </div>
          <div className="h-8 w-px" style={{ background: DP.border }} />
          {TRUST.map(([Icon, to, suffix, label, color]) => (
            <div key={label} className="flex items-center gap-2.5">
              <span className="flex h-10 w-10 items-center justify-center rounded-xl" style={{ background: `${color}14`, color }}><Icon size={18} /></span>
              <span>
                <span className="block text-base font-extrabold leading-none" style={{ color: DP.text }} data-testid={`stat-${label.toLowerCase().replace(/ /g, "-")}`}>
                  <Counter to={to} suffix={suffix} />
                </span>
                <span className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: DP.muted }}>{label}</span>
              </span>
            </div>
          ))}
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="py-16 sm:py-20" data-testid="categories-section">
        <div className="mx-auto max-w-7xl px-4">
          <div className="flex items-end justify-between">
            <SectionHead title="Shop by Category" center={false} />
            <Link to="/categories" data-testid="view-all-categories" className="hidden shrink-0 items-center gap-1 text-sm font-extrabold sm:flex" style={{ color: DP.royal }}>
              View All Categories <ArrowRight size={15} />
            </Link>
          </div>
          <div className="mt-8 grid grid-cols-3 gap-3 sm:grid-cols-5 sm:gap-4">
            {catCards.map((c, i) => {
              const art = CATEGORY_ART.find(([n]) => n === c.name);
              return (
                <Reveal key={c.name} delay={i * 0.05}>
                  <Link to={c.name === "More Categories" ? "/categories" : `/shop?category=${encodeURIComponent(c.name)}`}
                    data-testid={`category-card-${c.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className="group block rounded-2xl border bg-white p-4 text-center shadow-[0_4px_16px_rgba(11,25,44,0.05)] transition-all duration-300 hover:-translate-y-1.5 hover:shadow-[0_18px_40px_rgba(11,25,44,0.13)]"
                    style={{ borderColor: DP.border }}>
                    <div className="mx-auto flex h-16 w-16 items-center justify-center overflow-hidden rounded-2xl transition-transform duration-300 group-hover:scale-110 sm:h-20 sm:w-20"
                      style={{ background: `linear-gradient(135deg, ${DP.ice}, #fff)` }}>
                      {c.image
                        ? <img src={imgUrl(c.image)} alt={c.name} loading="lazy" className="h-full w-full object-cover" />
                        : <span className="text-3xl">{art ? art[1] : "🛍️"}</span>}
                    </div>
                    <p className="mt-2.5 text-[11px] font-extrabold leading-tight sm:text-xs" style={{ color: DP.text }}>{c.name}</p>
                    {c.count > 0 && <p className="text-[9px] font-semibold" style={{ color: DP.muted }}>{c.count} products</p>}
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* 3 PROMO CARDS */}
      <section className="py-6 sm:py-10" data-testid="promo-cards">
        <div className="mx-auto grid max-w-7xl gap-5 px-4 md:grid-cols-3">
          {PROMOS.map((pr, i) => (
            <Reveal key={pr.title} delay={i * 0.1}>
              <div data-testid={pr.testid}
                className="group flex h-full flex-col overflow-hidden rounded-3xl border bg-white shadow-[0_8px_30px_rgba(11,25,44,0.07)] transition-all duration-300 hover:-translate-y-1.5 hover:shadow-[0_26px_56px_rgba(11,25,44,0.16)]"
                style={{ borderColor: DP.border }}>
                <div className="overflow-hidden">
                  <img src={pr.img} alt={pr.title} loading="lazy" className="aspect-[16/10] w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                </div>
                <div className="flex flex-1 flex-col p-6">
                  <h3 className="text-lg font-extrabold" style={{ fontFamily: "Outfit, sans-serif", color: DP.text }}>{pr.title}</h3>
                  <p className="mt-2 flex-1 text-sm leading-relaxed" style={{ color: DP.muted }}>{pr.desc}</p>
                  <Link to={pr.to} data-testid={`${pr.testid}-cta`}
                    className="mt-4 inline-flex w-fit items-center gap-2 rounded-full px-5 py-2.5 text-xs font-extrabold text-white transition-transform hover:scale-105"
                    style={{ background: DP.royal }}>
                    {pr.cta} <ArrowRight size={13} />
                  </Link>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* TRENDING + MEGA OFFER */}
      <section className="py-16 sm:py-20" data-testid="trending-section">
        <div className="mx-auto max-w-7xl px-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <SectionHead title="Trending Products" center={false} />
            <Link to="/shop" data-testid="view-all-products" className="flex items-center gap-1 text-sm font-extrabold" style={{ color: DP.royal }}>View All Products <ArrowRight size={15} /></Link>
          </div>
          <div className="mt-6 flex gap-2 overflow-x-auto pb-1" data-testid="trending-tabs" style={{ scrollbarWidth: "none" }}>
            {tabCats.map((t) => (
              <button key={t} data-testid={`trend-tab-${t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} onClick={() => setTab(t)}
                className={`shrink-0 rounded-full px-4 py-2 text-xs font-extrabold transition-colors ${tab === t ? "text-white" : "border"}`}
                style={tab === t ? { background: DP.royal } : { borderColor: DP.border, color: DP.text }}>
                {t}
              </button>
            ))}
          </div>
          <div className="mt-6 grid gap-5 lg:grid-cols-4">
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:col-span-3">
              {shown.slice(0, 6).map((p) => <ProductCard key={p.id} p={p} />)}
              {!shown.length && (
                <div className="col-span-full rounded-2xl border border-dashed p-10 text-center text-sm" style={{ borderColor: DP.border, color: DP.muted }} data-testid="trending-empty">
                  Products are being added to the catalog — check back soon or explore partner plans.
                </div>
              )}
            </div>
            <Reveal className="lg:row-span-1">
              <div data-testid="mega-offer" className="relative flex h-full min-h-[320px] flex-col justify-end overflow-hidden rounded-3xl p-6 text-white shadow-[0_20px_50px_rgba(11,25,44,0.25)]"
                style={{ background: `linear-gradient(160deg, ${DP.navy} 20%, ${DP.royal})` }}>
                <img src={IMG.mega} alt="Mega discounts" loading="lazy" className="absolute inset-0 h-full w-full object-cover opacity-70" />
                <div className="absolute inset-0" style={{ background: `linear-gradient(to top, ${DP.navy} 8%, transparent 65%)` }} />
                <div className="relative">
                  <p className="text-[10px] font-extrabold uppercase tracking-[0.25em] text-emerald-300">Mega Discounts</p>
                  <p className="mt-1 text-4xl font-extrabold" style={{ fontFamily: "Outfit, sans-serif" }}>Up To 70% OFF</p>
                  <Link to="/deals" data-testid="mega-shop-now"
                    className="mt-4 inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-extrabold transition-transform hover:scale-105"
                    style={{ background: DP.green }}>
                    Shop Now <ArrowRight size={15} />
                  </Link>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-16 sm:py-20" style={{ background: DP.ice }} data-testid="how-it-works">
        <div className="mx-auto max-w-7xl px-4">
          <SectionHead eyebrow="Simple process" title="How DropPartner Works?" sub="Four steps from sign-up to profit — we handle the operational heavy lifting." />
          <div className="relative mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            <div className="absolute left-0 right-0 top-10 hidden h-0.5 lg:block" style={{ background: `linear-gradient(90deg, ${DP.royal}, ${DP.green})` }} />
            {STEPS.map(([n, t, d, color], i) => (
              <Reveal key={n} delay={i * 0.1}>
                <div className="relative rounded-2xl border bg-white p-6 text-center shadow-[0_6px_22px_rgba(11,25,44,0.06)] transition-all duration-300 hover:-translate-y-1.5 hover:shadow-[0_20px_44px_rgba(11,25,44,0.13)]"
                  style={{ borderColor: DP.border }} data-testid={`step-${n}`}>
                  <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl text-lg font-extrabold text-white shadow-lg" style={{ background: color, fontFamily: "Outfit, sans-serif" }}>{n}</span>
                  <h3 className="mt-4 text-sm font-extrabold" style={{ color: DP.text }}>{t}</h3>
                  <p className="mt-2 text-xs leading-relaxed" style={{ color: DP.muted }}>{d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* BIG CTA */}
      <section className="py-6 sm:py-10" data-testid="big-cta">
        <div className="mx-auto max-w-7xl px-4">
          <Reveal>
            <div className="relative flex flex-col items-center gap-8 overflow-hidden rounded-[2rem] p-8 sm:p-12 lg:flex-row"
              style={{ background: `linear-gradient(120deg, ${DP.navy}, #123B66)` }}>
              <div className="relative flex-1 text-center lg:text-left">
                <h2 className="text-3xl font-extrabold tracking-tight text-white sm:text-4xl" style={{ fontFamily: "Outfit, sans-serif" }}>
                  Ready To Start Your <span style={{ color: DP.green }}>Online Business?</span>
                </h2>
                <p className="mt-3 text-sm text-blue-200 sm:text-base">Start selling without the inventory headache. Join 10,000+ partners growing with DropPartner.</p>
                <div className="mt-6">
                  <GreenBtn to="/register" testid="cta-join-free">Join Now For Free</GreenBtn>
                </div>
              </div>
              <img src={IMG.rocket} alt="Launch your business" loading="lazy"
                className="relative w-52 rounded-3xl object-cover shadow-2xl sm:w-64" style={{ animation: "dpfloat 5s ease-in-out infinite" }} />
            </div>
          </Reveal>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-16 sm:py-20" data-testid="testimonials-section">
        <div className="mx-auto max-w-7xl px-4">
          <SectionHead eyebrow="Partner love" title="What Our Partners Say" />
          <div className="mt-10 grid gap-5 md:grid-cols-3">
            {TESTIMONIALS.map(([name, city, text, rating], i) => (
              <Reveal key={name} delay={i * 0.1}>
                <div className="flex h-full flex-col rounded-2xl border bg-white p-6 shadow-[0_6px_22px_rgba(11,25,44,0.06)] transition-shadow hover:shadow-[0_18px_44px_rgba(11,25,44,0.12)]"
                  style={{ borderColor: DP.border }} data-testid={`testimonial-${i}`}>
                  <Quote size={22} style={{ color: DP.green }} />
                  <p className="mt-3 flex-1 text-sm leading-relaxed" style={{ color: DP.muted }}>“{text}”</p>
                  <div className="mt-4 flex items-center gap-3 border-t pt-4" style={{ borderColor: DP.border }}>
                    <span className="flex h-10 w-10 items-center justify-center rounded-full text-sm font-extrabold text-white"
                      style={{ background: `linear-gradient(135deg, ${DP.royal}, ${DP.green})` }}>{name[0]}</span>
                    <div className="flex-1">
                      <p className="text-sm font-extrabold" style={{ color: DP.text }}>{name}</p>
                      <p className="text-[10px] font-semibold" style={{ color: DP.muted }}>{city}</p>
                    </div>
                    <Rating value={rating} />
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* BLOG PREVIEW */}
      <section className="py-16 sm:py-20" style={{ background: DP.ice }} data-testid="blog-preview">
        <div className="mx-auto max-w-7xl px-4">
          <div className="flex items-end justify-between">
            <SectionHead title="Latest From Our Blog" center={false} />
            <Link to="/blog" data-testid="view-all-blog" className="flex shrink-0 items-center gap-1 text-sm font-extrabold" style={{ color: DP.royal }}>View All <ArrowRight size={15} /></Link>
          </div>
          <div className="mt-8 grid gap-5 sm:grid-cols-3">
            {(blog?.items || []).slice(0, 3).map((b, i) => (
              <Reveal key={b.id} delay={i * 0.08}>
                <Link to={`/blog/${b.slug}`} data-testid={`blog-card-${b.slug}`}
                  className="group block overflow-hidden rounded-2xl border bg-white shadow-[0_6px_22px_rgba(11,25,44,0.06)] transition-all duration-300 hover:-translate-y-1.5 hover:shadow-[0_20px_44px_rgba(11,25,44,0.13)]"
                  style={{ borderColor: DP.border }}>
                  <div className="overflow-hidden"><img src={b.cover} alt={b.title} loading="lazy" className="aspect-[16/9] w-full object-cover transition-transform duration-500 group-hover:scale-105" /></div>
                  <div className="p-5">
                    <span className="rounded-full px-2.5 py-1 text-[9px] font-extrabold uppercase tracking-wider text-white" style={{ background: DP.green }}>{b.category}</span>
                    <h3 className="mt-3 line-clamp-2 text-sm font-extrabold leading-snug" style={{ color: DP.text }}>{b.title}</h3>
                    <p className="mt-2 text-[10px] font-semibold" style={{ color: DP.muted }}>{fmtDate(b.published_at)} · {b.read_time}</p>
                    <span className="mt-3 inline-flex items-center gap-1 text-xs font-extrabold" style={{ color: DP.royal }}>Read More <ArrowRight size={12} /></span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </SiteShell>
  );
}
